import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { fmtDate, DOC_STATUS_STYLES } from "@/lib/format";
import { FileText, FileWarning, CheckCircle2, ArrowRight, X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Missing", "Pending", "Uploaded", "Received", "Not Required"];

export default function Documents() {
  const [jobs, setJobs] = useState([]);
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ customer: "all", owner: "all" });
  const navigate = useNavigate();

  const status = params.get("status") || "all";
  const setStatus = (s) => {
    if (s === "all") params.delete("status");
    else params.set("status", s);
    setParams(params, { replace: true });
  };

  useEffect(() => {
    client.get("/shipments").then((r) => setJobs(r.data.filter((j) => j.current_stage !== "Closed")));
  }, []);

  const owners = useMemo(() => [...new Set(jobs.map((j) => j.assigned_owner))].sort(), [jobs]);
  const customers = useMemo(() => [...new Set(jobs.map((j) => j.customer_name))].sort(), [jobs]);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const filtered = jobs.filter((j) => {
    if (status !== "all" && !j.documents.some((d) => d.status === status)) return false;
    if (filters.customer !== "all" && j.customer_name !== filters.customer) return false;
    if (filters.owner !== "all" && j.assigned_owner !== filters.owner) return false;
    return true;
  });

  const counts = (s) => jobs.filter((j) => j.documents.some((d) => d.status === s)).length;
  const anyFilter = filters.customer !== "all" || filters.owner !== "all";

  return (
    <Layout>
      <div className="mb-5">
        <p className="op-label mb-1">Document control</p>
        <h1 className="font-heading text-2xl font-semibold text-ink flex items-center gap-2">
          Manifests &amp; PODs
        </h1>
        <p className="text-sm text-ink-2 mt-1">Required documents per shipment — what's received, pending, and missing.</p>
      </div>

      {/* Status quick filters */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button data-testid="doc-filter-all" onClick={() => setStatus("all")} className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors ${status === "all" ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"}`}>All Jobs</button>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`doc-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setStatus(s)}
            className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors flex items-center gap-1.5 ${status === s ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"}`}
          >
            {s}
            <span className={`font-mono-data text-[10.5px] ${status === s ? "text-white/70" : "text-ink-3"}`}>{counts(s)}</span>
          </button>
        ))}
      </div>

      <div className="bg-surface border border-line rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[160px]">
          <label className="op-label block mb-1">Customer</label>
          <Select value={filters.customer} onValueChange={(v) => set("customer", v)}>
            <SelectTrigger data-testid="doc-customer-filter" className="h-9 border-line rounded-sm bg-surface text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <label className="op-label block mb-1">Owner</label>
          <Select value={filters.owner} onValueChange={(v) => set("owner", v)}>
            <SelectTrigger data-testid="doc-owner-filter" className="h-9 border-line rounded-sm bg-surface text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {anyFilter && (
          <button data-testid="doc-clear-filters" onClick={() => setFilters({ customer: "all", owner: "all" })} className="h-9 px-2.5 rounded-sm text-[13px] text-ink-2 hover:text-danger flex items-center gap-1 transition-colors"><X className="h-3.5 w-3.5" /> Clear</button>
        )}
        <div className="ml-auto text-[13px] text-ink-2 font-mono-data">{filtered.length} shown</div>
      </div>

      <div className="bg-surface border border-line rounded-md overflow-hidden">
        <div className="overflow-x-auto mv-scroll">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-alt">
                {["Job #", "Customer", "Documents", "Missing", "Owner", "Delivery Due", ""].map((h) => (
                  <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {filtered.map((j) => {
                const missing = j.documents.filter((d) => d.status === "Missing");
                const ready = j.documents.filter((d) => ["Received", "Uploaded"].includes(d.status)).length;
                return (
                  <tr key={j.id} data-testid={`doc-row-${j.job_number}`} tabIndex={0} onClick={() => navigate(`/app/shipments/${j.id}?focus=documents`)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/app/shipments/${j.id}?focus=documents`); } }} className="hover:bg-surface-alt transition-colors cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel/40">
                    <td className="px-4 py-3 font-mono-data text-[12.5px] text-ink font-medium whitespace-nowrap">{j.job_number}</td>
                    <td className="px-4 py-3 text-[13.5px] text-ink whitespace-nowrap">{j.customer_name}</td>
                    <td className="px-4 py-3 font-mono-data text-[12px] text-ink-2 whitespace-nowrap">{ready}/{j.documents.length} ready</td>
                    <td className="px-4 py-3">
                      {missing.length > 0 ? (
                        <div className="flex flex-col gap-0.5 max-w-[260px]">
                          <span data-testid={`doc-missing-count-${j.job_number}`} className="text-[12.5px] font-medium text-danger whitespace-nowrap">{missing.length} missing</span>
                          <span className="text-[11.5px] text-ink-2 truncate">{missing.map((d) => d.type).join(" · ")}</span>
                        </div>
                      ) : (
                        <span className="text-[12.5px] text-ink-3">Complete</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.assigned_owner}</td>
                    <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{fmtDate(j.delivery_due_date)}</td>
                    <td className="px-4 py-3"><ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-ink-3">No jobs match this document filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
